import moment from "moment";
import { useSelector } from "react-redux";
import React, { useEffect, useContext, useState } from "react";
import { useParams, useHistory } from "react-router";
import { Card, CardBody, CardHeader } from "reactstrap";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  MenuItem,
} from "@mui/material";

import { ReactComponent as BackIcon } from "assets/img/svg/BackArrow.svg";
import { ReactComponent as ExpandMoreIcon } from "assets/img/svg/fi-chevron-down.svg";
import { ReactComponent as UserList } from "assets/img/svg/UserList.svg";
import { ReactComponent as ClipboardText } from "assets/img/svg/ClipboardText.svg";
import { ReactComponent as Newspaper } from "assets/img/svg/Newspaper.svg";
import { ReactComponent as UserSwitch } from "assets/img/svg/UserSwitch.svg";
import { ReactComponent as MoneyWavy } from "assets/img/svg/MoneyWavy.svg";
import { ReactComponent as Bank } from "assets/img/svg/Bank.svg";
import { ReactComponent as LinkSimple } from "assets/img/svg/LinkSimple.svg";
import { ReactComponent as Clock } from "assets/img/svg/ClockCounterClockwise.svg";
import { ReactComponent as Table } from "assets/img/svg/Table.svg";
import { ReactComponent as Student } from "assets/img/svg/Student.svg";

import { PermissionContext } from "appContext";
import ALoader from "views/pages/manage/common/a-loader";
import AChip from "views/pages/manage/common/chip/AChip";
import ADataGrid from "views/pages/manage/common/data-grid/ADataGrid";
import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import { LastUpdatedDetails } from "views/pages/manage/employeeManagement/userProfile/helper";
import { capitalize } from "../../common/utils/methods/commonMethods/utilityMethod";
import CustomDownload from "../../common/commonComponents/CustomDownload";

import {
  getVendorDetails,
  getVendorAuditLogs,
  VENDOR_TYPE_ENUM,
  retrySAPsync,
  getLanguageDropdown,
} from "./helper";
import ApproveDialog from "./creation-approval/ApproveDialog";
import RejectDialog from "./creation-approval/RejectDialog";
import {
  actionableApproverLevelMap,
  approvalLevelMap,
  fetchVendorApprovalConfigs,
  getMaxApproverLevel,
  rejectionLevelMap,
} from "./admission-advisor-dashboard/utils";
import {
  editTransactionStatus,
  getApprovalStepperSteps,
} from "./creation-approval/helper";
import AButton from "../../common/form-fields-mui/AButton";
import TransferTimeline from "modules/studentView/progressTimeLine";

const VendorInfo = () => {
  const { id } = useParams();
  const history = useHistory();
  const userRole = useContext(PermissionContext);
  const masterDropdowns = useSelector(
    (state) => state.globalData.masterDropdowns,
  );

  const [languageOptions, setLanguageOptions] = useState(
    masterDropdowns?.languages || [],
  );
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [maxLevel, setMaxLevel] = useState(4);
  const [expanded, setExpanded] = useState({ 0: true }); // Vendor Details expanded by default

  // Dialog state
  const [approveSuccessDialog, setApproveSuccessDialog] = useState({
    isOpen: false,
    data: null,
  });
  const [rejectDialog, setRejectDialog] = useState({
    isOpen: false,
    data: null,
  });
  // Role-based action enable/disable
  const [canApprove, setCanApprove] = useState(false);
  const [canReject, setCanReject] = useState(false);

  useEffect(() => {
    // if (!masterDropdowns?.languages.length) {
    fetchDropdown();
    // }
  }, []);

  const formatLanguageDropdown = (response) => {
    const options = response.map((item) => ({
      label: `${item.languageDispValue}`,
      value: item.id,
    }));

    setLanguageOptions(options);
  };

  const fetchDropdown = async () => {
    // setDropdownLoader(true);
    const res = await getLanguageDropdown();
    formatLanguageDropdown(res);

    // setDropdownLoader(false);
  };

  // On mount and when pagination changes
  useEffect(() => {
    if (!id) return;
    fetchAPIs();
  }, [id, userRole]);

  // Fetch all data
  const fetchAPIs = async () => {
    setLoading(true);
    try {
      // Step 1: Fetch payment order info and logs
      const [vendorRes, logsRes] = await Promise.all([
        getVendorDetails(id),
        getVendorAuditLogs(id, 100, 0), // fetch all logs, slice in UI
      ]);

      const vendorData = vendorRes || null;
      setVendorData(vendorData);
      // Update logs table
      const logsList =
        logsRes && logsRes.auditLogsList ? logsRes.auditLogsList : [];
      setAuditLogs(logsList);

      // Step 2: Fetch approval configs using transaction Data
      let configsRes = null;
      if (
        vendorData &&
        vendorData.onboardingBusinessAreaId &&
        vendorData.vendorType
      ) {
        const approvalConfigPayload = {
          businessAreaIds: [vendorData.onboardingBusinessAreaId],
          // yearIds: [vendorData.academicYearId],
          vendorTypes: ["VENDOR_CREATION"],
        };

        configsRes = await fetchVendorApprovalConfigs(approvalConfigPayload);
      }

      setMaxLevel(getMaxApproverLevel(configsRes?.data?.data?.[0], logsList));

      // Role-based logic for Approve/Reject/Edit
      let enableApprove = false,
        enableReject = false;

      // Only allow actions for actionable statuses
      const currentLevel = vendorData
        ? actionableApproverLevelMap[vendorData.status]
        : null;
      if (currentLevel && vendorData && configsRes?.data?.data?.length) {
        const config = configsRes.data.data[0];
        const levelRoles = config.roleLevelApprovals?.[currentLevel] || [];
        if (levelRoles.some((r) => r.roleDispVal === userRole.roleType)) {
          enableApprove = true;
          enableReject = true;
        }
      }
      setCanApprove(enableApprove);
      setCanReject(enableReject);
    } catch (err) {
      setVendorData(null);
      setAuditLogs([]);
      setCanApprove(false);
      setCanReject(false);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  const steps = [
    { text: "Vendor Details", icon: <UserList /> },
    { text: "Basic Information", icon: <ClipboardText /> },
    { text: "Onboarding Details", icon: <Newspaper /> },
    { text: "Reference Details", icon: <UserSwitch /> },
    { text: "Payments and Tax Details", icon: <MoneyWavy /> },
    { text: "Withholding Tax Data", icon: <Bank /> },
    { text: "Agreements and Attachments", icon: <LinkSimple /> },
    { text: "Logs", icon: <Clock /> },
  ];

  if (loading) {
    return <ALoader />;
  }

  if (!vendorData) {
    return <div>No data found</div>;
  }

  const approvalJourney = getApprovalStepperSteps(auditLogs || [], maxLevel);

  // Approve handler
  const handleApprove = async () => {
    const approvalStatus = approvalLevelMap[vendorData.status];
    const payload = {
      approvalStatus,
      approvalRemarks: "",
    };

    try {
      const res = await editTransactionStatus(vendorData?.id, payload);
      if (res?.code === 200) {
        setApproveSuccessDialog({
          isOpen: true,
          data: { ...res, ...vendorData, approvalStatus },
        });

        fetchAPIs();
      }
    } catch (err) {
      // handle error (show toast or dialog)
    }
  };

  // Reject handler
  const handleReject = async (remarks = "") => {
    const approvalStatus = rejectionLevelMap[vendorData.status];
    const payload = {
      approvalStatus,
      approvalRemarks: remarks,
    };

    try {
      const res = await editTransactionStatus(vendorData?.id, payload);
      if (res?.code === 200) {
        setApproveSuccessDialog({
          isOpen: true,
          data: { ...res, ...vendorData, remarks, approvalStatus },
          approvalStatus,
        });

        fetchAPIs();
      }
    } catch (err) {
      // handle error (show toast or dialog)
    }
  };

  // Reject dialog handler
  const handleRejectDialog = (remarks) => {
    handleReject(remarks);
    setRejectDialog({ isOpen: false, data: null });
  };

  const retrySapSync = async () => {
    const res = await retrySAPsync(vendorData?.id, "VENDOR_CREATION");
    if (res?.code === 200) {
      window.location.reload();
    }
  };

  return (
    <div className="large-text mx-4 my-3 pb-4 label-color">
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <IconButtonWrapper onClick={() => window.history.back()}>
            <BackIcon />
          </IconButtonWrapper>
          {vendorData.vendorFirstName} {vendorData.vendorLastName} (
          {vendorData.panNumber})
        </div>
        <Card className="p-2 text-sm flex-row align-items-center">
          <div style={{ color: "#AFAFAF" }}> Vendor Type: &nbsp;</div>
          <span className="text-bold">
            {capitalize(vendorData.vendorType) || "-"}
          </span>
        </Card>
      </div>
      <div className="mx-4 mt-4 d-flex justify-space-between">
        <div style={{ width: "65%" }} className="mr-4">
          {steps.map((step, index) => (
            <Accordion
              key={index}
              expanded={expanded[index] || false}
              onChange={handleChange(index)}
              className="mb-3"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon fill="#000" />}>
                {step.icon}
                <Typography className="ml-2">{step.text}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {index === 0 && <VendorDetailsSection data={vendorData} />}
                {index === 1 && (
                  <BasicInformationSection
                    data={vendorData}
                    languageOptions={languageOptions}
                  />
                )}
                {index === 2 && <OnboardingDetailsSection data={vendorData} />}
                {index === 3 && <ReferenceDetailsSection data={vendorData} />}
                {index === 4 && <PaymentsTaxDetailsSection data={vendorData} />}
                {index === 5 && <WithholdingTaxDataSection data={vendorData} />}
                {index === 6 && (
                  <AgreementsAttachmentsSection data={vendorData} />
                )}
                {index === 7 && (
                  <LogsSection
                    auditLogs={auditLogs}
                    loading={loading}
                    vendorData={vendorData}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
        <div style={{ width: "30%" }}>
          <Card className="mb-3">
            <CardBody className="px-4 py-3">
              <div className="text-bold mb-2" style={{ fontSize: 16 }}>
                Request actions
              </div>

              <div className="d-flex flex-column gap-2 w-100">
                <AButton
                  variant="primary_filled"
                  block
                  disabled={!canApprove || loading}
                  onClick={handleApprove}
                >
                  Approve
                </AButton>
                <AButton
                  variant="danger_filled"
                  block
                  disabled={!canReject || loading}
                  onClick={() =>
                    setRejectDialog({ isOpen: true, data: vendorData })
                  }
                >
                  Reject
                </AButton>

                {vendorData.status === "SAP_SYNC_FAILED" && (
                  <AButton
                    variant="primary_filled"
                    block
                    onClick={retrySapSync}
                  >
                    Retry SAP Sync
                  </AButton>
                )}
              </div>
            </CardBody>
          </Card>

          {/* Approval journey */}
          <TransferTimeline
            steps={approvalJourney}
            heading="Creation request status"
            marginLeft="0px"
            // marginBottom="0px"
          />

          <Card>
            <CardHeader>Actions</CardHeader>
            <CardBody className="p-2">
              <MenuItem
                className="d-flex py-2"
                style={{ gap: "16px", fontWeight: 700 }}
                onClick={() =>
                  history.push(
                    `/admin/vendorPsidMapping/view/${id}/true/${vendorData.vendorType}`,
                  )
                }
              >
                <Table />
                View Payment History
              </MenuItem>
              <MenuItem
                className="d-flex py-2"
                style={{ gap: "16px", fontWeight: 700 }}
                onClick={() => history.push(`/admin/vendorPsidMapping/${id}`)}
                disabled={
                  vendorData?.status?.toLowerCase() === "inactive" ||
                  ![
                    "CR_VENDOR_PAYMENT_APPROVER",
                    "SUPER_ADMIN",
                    "CR_ERP_SUPPORT_HEAD",
                    "CR_ERP_SUPPORT_L2",
                    "CR_ERP_SUPPORT_L1",
                  ].includes(userRole.roleType) ||
                  vendorData?.vendorType.toLowerCase() === "invigilator"
                }
              >
                <Student />
                Map Student PSIDs
              </MenuItem>
              <MenuItem
                className="d-flex py-2"
                style={{ gap: "16px", fontWeight: 700 }}
                onClick={() =>
                  history.push(
                    `/admin/vendorPsidMapping/view/${id}/false/${vendorData.vendorType}`,
                  )
                }
                disabled={
                  // !["CR_VENDOR_PAYMENT_APPROVER"].includes(userRole.roleType) ||
                  vendorData?.vendorType.toLowerCase() === "invigilator"
                }
              >
                <Table />
                View Mapped Students
              </MenuItem>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Approval Dialogs */}
      {approveSuccessDialog.isOpen && (
        <ApproveDialog
          confirmDialog={approveSuccessDialog}
          setConfirmDialog={setApproveSuccessDialog}
        />
      )}
      {rejectDialog.isOpen && (
        <RejectDialog
          isOpen={rejectDialog.isOpen}
          data={rejectDialog.data}
          setRejectionDialog={setRejectDialog}
          rejectionHandler={handleRejectDialog}
        />
      )}
    </div>
  );
};

// Section Components
const VendorDetailsSection = ({ data }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "15px",
      fontSize: "14px",
    }}
  >
    <div>
      <span style={{ color: "#757575" }}>Vendor Type:</span>
      <br />
      <span className="text-bold">
        {VENDOR_TYPE_ENUM[data.vendorType] || "-"}
      </span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>PAN Card Number:</span>
      <br />
      <span className="text-bold">{data.panNumber || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>SAP Vendor ID:</span>
      <br />
      <span className="text-bold">{data.vendorSapCode || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Account Status:</span>
      <br />
      <span className="text-bold">{data.status || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Onboarding date:</span>
      <br />
      <span className="text-bold">
        {data.onboardingDate
          ? moment(data.onboardingDate).format("DD-MMM-YYYY")
          : "-"}
      </span>
    </div>
  </div>
);

const BasicInformationSection = ({ data, languageOptions }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "15px",
      fontSize: "14px",
    }}
  >
    <div>
      <span style={{ color: "#757575" }}>First Name:</span>
      <br />
      <span className="text-bold">{data.vendorFirstName || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Last Name:</span>
      <br />
      <span className="text-bold">{data.vendorLastName || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Email ID:</span>
      <br />
      <span className="text-bold">{data.emailId || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Mobile Number:</span>
      <br />
      <span className="text-bold">{data.phoneNumber || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Language:</span>
      <br />
      <span className="text-bold">
        {data?.lang
          ? languageOptions.find((obj) => obj.value == data.lang)?.label
          : "English"}
      </span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Address Line 1:</span>
      <br />
      <span className="text-bold">{data.addressLine1 || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Address Line 2:</span>
      <br />
      <span className="text-bold">{data.addressLine2 || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Address Line 3:</span>
      <br />
      <span className="text-bold">{data.addressLine3 || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Address Line 4:</span>
      <br />
      <span className="text-bold">{data.addressLine4 || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Pincode:</span>
      <br />
      <span className="text-bold">{data.pincode || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>City:</span>
      <br />
      <span className="text-bold">{data.city || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>State:</span>
      <br />
      <span className="text-bold">{data.state || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Country:</span>
      <br />
      <span className="text-bold">{data.country || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Occupation:</span>
      <br />
      <span className="text-bold">{data.occupation}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Organisation:</span>
      <br />
      <span className="text-bold">{data?.organization || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Designation:</span>
      <br />
      <span className="text-bold">{data?.designation || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>GSTIN:</span>
      <br />
      <span className="text-bold">{data?.gstin?.trim() || "-"}</span>
    </div>
  </div>
);

const OnboardingDetailsSection = ({ data }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "15px",
      fontSize: "14px",
    }}
  >
    <div>
      <span style={{ color: "#757575" }}>BU Code:</span>
      <br />
      <span className="text-bold">
        {data.onboardingBusinessAreaDispVal || "-"}
      </span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>SAP Plant ID:</span>
      <br />
      <span className="text-bold">{data.onboardingSapPlantCode || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Cost Code:</span>
      <br />
      <span className="text-bold">{data.onboardingCostCode || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Employee ID:</span>
      <br />
      <span className="text-bold">{data.onboardingEmployeeId || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Name:</span>
      <br />
      <span className="text-bold">{data.onboardingEmployeeName || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Designation:</span>
      <br />
      <span className="text-bold">
        {data.onboardingEmployeeDesignation || "-"}
      </span>
    </div>
  </div>
);

const ReferenceDetailsSection = ({ data }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "15px",
      fontSize: "14px",
    }}
  >
    <div>
      <span style={{ color: "#757575" }}>Employee ID:</span>
      <br />
      <span className="text-bold">{data.referenceEmployeeId || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Name:</span>
      <br />
      <span className="text-bold">{data.referenceEmployeeName || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Designation:</span>
      <br />
      <span className="text-bold">
        {data.referenceEmployeeDesignation || "-"}
      </span>
    </div>
  </div>
);

const PaymentsTaxDetailsSection = ({ data }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "15px",
      fontSize: "14px",
    }}
  >
    <div>
      <span style={{ color: "#757575" }}>Preferred Payment Method:</span>
      <br />
      <span className="text-bold">{data.preferredPaymentMethod || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Bank Code:</span>
      <br />
      <span className="text-bold">{data.bankName || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Bank Branch:</span>
      <br />
      <span className="text-bold">{data.bankBranch || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Account Holder Name:</span>
      <br />
      <span className="text-bold">{data.accountHolderName || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Account Number:</span>
      <br />
      <span className="text-bold">{data.bankAccount || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>IFSC Code:</span>
      <br />
      <span className="text-bold">{data.ifsc || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Account Type:</span>
      <br />
      <span className="text-bold">{data.accountType || "-"}</span>
    </div>
  </div>
);

const WithholdingTaxDataSection = ({ data }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "15px",
      fontSize: "14px",
    }}
  >
    <div>
      <span style={{ color: "#757575" }}>Title:</span>
      <br />
      <span className="text-bold">{data.withholdingTaxTitle || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Withholding Tax Type:</span>
      <br />
      <span className="text-bold">{data.withholdingTaxType || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Withholding Tax Code:</span>
      <br />
      <span className="text-bold">{data.withholdingTaxCode || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Default Jurisdiction:</span>
      <br />
      <span className="text-bold">
        {data.withholdingTaxDefaultJurisdiction || "-"}
      </span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Recipient Type:</span>
      <br />
      <span className="text-bold">
        {data.withholdingTaxRecipientType || "-"}
      </span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Payment Term:</span>
      <br />
      <span className="text-bold">{data.withholdingPaymentTerm || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Account Group:</span>
      <br />
      <span className="text-bold">{data.withholdingAccountGroup || "-"}</span>
    </div>
    <div>
      <span style={{ color: "#757575" }}>Vendor Class:</span>
      <br />
      <span className="text-bold">{data.withholdingVendorClass || "-"}</span>
    </div>
  </div>
);

const AgreementsAttachmentsSection = ({ data, loading }) => {
  const attachmentColumns = [
    new AGridColDef("attachmentType", "Type").setMinWidth(180),

    new AGridColDef("attachmentName", "Attachment")
      .setMinWidth(150)
      .renderCellComponent((params) => {
        return (
          <Tooltip title={params.value}>
            <span>
              <CustomDownload
                downloadKey={params.row.attachmentKey}
                text={params.value}
                isIcon={false}
              />
            </span>
          </Tooltip>
        );
      }),

    new AGridColDef("effectiveDate", "Effective Date")
      // .setMinWidth(150)
      .renderCellComponent((params) =>
        params.value ? moment(params.value).format("DD-MMM-YYYY") : "-",
      ),

    new AGridColDef("expiryDate", "Expiry Date")
      // .setMinWidth(150)
      .renderCellComponent((params) =>
        params.value ? moment(params.value).format("DD-MMM-YYYY") : "-",
      ),

    new AGridColDef("status", "Status")
      // .setMinWidth(120)
      .columnAlign("right")
      .renderCellComponent((params) => (
        <AChip
          label={params.row.isDeleted ? "Inactive" : "Approved"}
          success={!params.row.isDeleted}
        />
      )),
  ];

  return (
    <div>
      <ADataGrid
        rows={data?.attachments || []}
        columns={attachmentColumns}
        rowId={(row) => row.id}
        loading={loading}
        height="300px"
      />
    </div>
  );
};

const LogsSection = ({ auditLogs, loading, vendorData }) => {
  const auditColumns = [
    new AGridColDef("event", "Event").setMinWidth(200),

    new AGridColDef("updatedOn", "Updated On")
      .setMinWidth(350)
      .renderCellComponent((params) => {
        const date = params?.row?.updatedOn || params?.row?.createdOn;
        const by = params?.row?.updatedBy || params?.row?.createdBy;
        const displayText = date ? LastUpdatedDetails(date, by) : "-";
        return (
          <Tooltip title={displayText}>
            <span>{displayText}</span>
          </Tooltip>
        );
      }),

    new AGridColDef("oldValue", "Old Value")
      .setMinWidth(180)
      .renderCellComponent((params) =>
        params.value ? (
          <Tooltip title={params.value}>
            <span>{params.value}</span>
          </Tooltip>
        ) : (
          params.value || "-"
        ),
      ),
    new AGridColDef("newValue", "New Value")
      .setMinWidth(180)
      .renderCellComponent((params) =>
        params.value ? (
          <Tooltip title={params.value}>
            <span>{params.value}</span>
          </Tooltip>
        ) : (
          params.value || "-"
        ),
      ),

    new AGridColDef("status", "Status")
      .setMinWidth(80)
      .columnAlign("right")
      .renderCellComponent((params) => (
        <AChip
          label={vendorData.status ? "Active" : "Inactive"}
          success={vendorData.status}
        />
      )),
  ];

  return (
    <div>
      <ADataGrid
        rows={auditLogs}
        columns={auditColumns}
        rowId={(row) => row.id}
        loading={loading}
        height="300px"
      />
    </div>
  );
};

export default VendorInfo;
